import { postModuleKeys } from '@/shared/helpers/module-keys';
import { IFrameNav, IFrameNavAccessConfig } from '@/shared';

/*
  IFRAME NAVIGATION CONFIG
  ------------------------------------------------------------------------------
  This file maps URL patterns to internal navigation bars via IFRAME_NAV_RULES.
  Matches are injected into 'route.meta.iframeNav' and rendered by RsPartialIFrameNavBar.

  LOGIC & BEHAVIOR:
  - Permissions: A tab is hidden if the user lacks 'access' permissions. However,
    if a tab has 'children', it will remain visible as long as at least one
    child is accessible (the parent "inherits" visibility).
  - Active State: The navbar first tries to match the current 'route.name' to the route key, e.g. CityPages.
    If no match is found, it falls back to checking if 'activePath' is a substring of the current URL.
  - Dropdowns: Tabs with 'children' act as dropdown toggles. Clicking the
    parent will NOT navigate; only clicking a child triggers a route change.

  PROPERTY REFERENCE:
  - homeRoute:    (Optional) Named route for the home icon. If omitted, icon is hidden.
  - homeIcon:     (Optional) FontAwesome class. Defaults to 'fa-duotone fa-house'.
  - includeTitle: (Optional) Shows the global sidebar title (e.g., Website Name) next to icon.
  - activePath:   (Optional) URL substring(s) to force-active a tab. Accepts a string or array of strings.
  - access:       Permission config (key, keyType, accessType, idParam). 'key' can be
                  an array.

  After defining a new config object, you MUST add a matching regex rule to the 'IFRAME_NAV_RULES'
  array at the bottom of this file to enable it.
  ------------------------------------------------------------------------------
*/

const baseSiteAccess: IFrameNavAccessConfig = {
  key: 'sites',
  keyType: 'site',
  accessType: 'read',
  idParam: 'websiteId',
};

const userManagementAccess: IFrameNavAccessConfig = {
  key: 'client_settings',
  keyType: 'account',
  accessType: 'read',
};

const userManagementIframeNav: IFrameNav = {
  homeRoute: 'SettingsUserManagement',
  homeIcon: 'fa-sharp fa-solid fa-users-gear',
  tabs: [
    {
      label: 'User Groups',
      labelKey: 'dataTableFields.userGroups',
      route: 'SettingsUserGroups',
      activePath: '/user-groups',
      access: userManagementAccess,
    },
    {
      label: 'Users',
      labelKey: 'dataTableFields.users',
      route: 'SettingsUsers',
      activePath: '/users',
      access: userManagementAccess,
    },
  ],
};

const websitesIframeNav: IFrameNav = {
  homeRoute: 'Websites',
  homeIcon: 'fa-sharp fa-solid fa-globe',
  includeTitle: true,
  tabs: [
    {
      label: 'Menu',
      labelKey: 'website.navigation.menu',
      route: 'Menu',
      access: { ...baseSiteAccess, key: 'menu_editor' },
    },
    {
      label: 'Pages',
      labelKey: 'website.navigation.pages',
      route: 'Pages',
      access: { ...baseSiteAccess, key: 'pages' },
    },
    {
      label: 'Posts',
      labelKey: 'website.navigation.posts',
      route: 'Posts',
      activePath: '/posts',
      access: { ...baseSiteAccess, key: postModuleKeys },
    },
    {
      label: 'City Pages',
      labelKey: 'website.navigation.cityPages',
      route: 'CityPages',
      access: { ...baseSiteAccess, key: 'cities' },
    },
    {
      label: 'Global Content',
      labelKey: 'website.navigation.globalContent',
      route: 'WebsiteCustomFields',
      access: { ...baseSiteAccess, key: 'site_management' },
    },
    {
      label: 'Website Settings',
      labelKey: 'website.navigation.websiteSettings',
      route: 'WebsiteSettings',
      activePath: '/settings/global',
      access: { ...baseSiteAccess, key: 'site_management' },
    },
    {
      label: 'Forms',
      labelKey: 'website.navigation.forms',
      route: 'Forms',
      activePath: ['/forms', '/inquiries'],
      access: { ...baseSiteAccess, key: ['form_settings', 'inquiries'] },
      children: [
        {
          label: 'Form Submissions',
          labelKey: 'website.navigation.formSubmissions',
          route: 'FormSubmissions',
          access: { ...baseSiteAccess, key: 'inquiries' },
        },
        {
          label: 'Form Settings',
          labelKey: 'website.navigation.formSettings',
          route: 'WebsiteFormSettingsAlt',
          access: { ...baseSiteAccess, key: 'form_settings' },
        },
      ],
    },
  ],
};

const baseAccountingAccess: IFrameNavAccessConfig = {
  key: 'accounting',
  keyType: 'account',
  accessType: 'read',
};

const accountingIframeNav: IFrameNav = {
  tabs: [
    {
      label: 'Billing',
      labelKey: 'billing.navigation.billing',
      route: 'SettingsBilling',
      access: baseAccountingAccess,
    },
    {
      label: 'Invoices',
      labelKey: 'billing.navigation.invoices',
      route: 'SettingsInvoices',
      access: baseAccountingAccess,
    },
    {
      label: 'Accounting Overview',
      labelKey: 'billing.navigation.accountingOverview',
      route: 'SettingsAccounting',
      access: baseAccountingAccess,
    },
  ],
};

export const IFRAME_NAV_RULES: { test: RegExp; nav: IFrameNav }[] = [
  {
    test: /^\/websites\/[^/]+\//,
    nav: websitesIframeNav,
  },
  {
    test: /^\/settings\/(accounting|billing|invoices)/,
    nav: accountingIframeNav,
  },
  {
    test: /^\/settings\/user-management\/(user-groups|users)\/?$/,
    nav: userManagementIframeNav,
  },
  // e.g. { test: /^\/tours\//, nav: toursIframeNav },
];
